import { memo, useCallback, useMemo } from 'react';
import { TranscriptSelection } from '../types';
import { Word } from '@/player-config/src/types/transcript-types';
import IconButton from '@/components/atoms/Button/IconButton';
import TooltipV2 from '@/components/TooltipV2';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { getCaptionsForSegment } from '@/player-config/src/utils/playerConfigUtils';
import { getAllCompositeSegments } from '@/player-config/src/utils/clipUtils';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/constants';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

function TranscriptHideMenuOptions({
  transcriptSelection,
  onCloseMenu,
  onEditActionTriggered
}: {
  transcriptSelection?: TranscriptSelection | null;
  onCloseMenu: () => void;
  onEditActionTriggered: () => void;
}) {
  const { clipData, clipCaptionsLoading } = useClipsContext();

  const compositeSegmentIds = useMemo(() => {
    const ids = getAllCompositeSegments(clipData).map(s => s.id);
    return ids.length > 0 ? new Set(ids) : null;
  }, [clipData]);

  const getRelevantWordEntries = useCallback(
    (words: Record<string, Word[]>) => {
      const entries = Object.entries(words);
      return compositeSegmentIds ? entries.filter(([id]) => compositeSegmentIds.has(id)) : entries;
    },
    [compositeSegmentIds]
  );

  const { areAllSelectedWordsHidden, areAllSelectedWordsVisible } = useMemo(() => {
    if (!transcriptSelection || !transcriptSelection.startSegmentId) {
      return { areAllSelectedWordsHidden: false, areAllSelectedWordsVisible: false };
    }
    let allHidden = true;
    let allVisible = true;
    for (const [segmentId, selectedWords] of getRelevantWordEntries(transcriptSelection.words)) {
      if (!selectedWords.length) {
        continue;
      }
      const minTime = Math.min(...selectedWords.map(w => +w.start_time));
      const maxTime = Math.max(...selectedWords.map(w => +w.end_time));
      const captions = getCaptionsForSegment({
        clip: clipData,
        segmentId: segmentId,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      }).filter(w => +w.start_time >= minTime && +w.end_time <= maxTime);
      if (!captions.every(w => w.hidden)) {
        allHidden = false;
      }
      if (captions.some(w => w.hidden)) {
        allVisible = false;
      }
    }
    return { areAllSelectedWordsHidden: allHidden, areAllSelectedWordsVisible: allVisible };
  }, [clipData, transcriptSelection, getRelevantWordEntries]);

  const updateWordVisibility = useCallback(
    (actionType: typeof CLIP_UPDATE_TYPE.HIDE_WORD | typeof CLIP_UPDATE_TYPE.SHOW_WORD) => {
      onEditActionTriggered();
      getRelevantWordEntries(transcriptSelection?.words ?? {}).forEach(([segmentId, words]) => {
        updateClipData({ actionType, words, clipId: clipData.id, compositeId: segmentId });
      });
      onCloseMenu();
    },
    [clipData.id, onCloseMenu, onEditActionTriggered, transcriptSelection?.words, getRelevantWordEntries]
  );

  const hideInTranscript = useCallback(() => updateWordVisibility(CLIP_UPDATE_TYPE.HIDE_WORD), [updateWordVisibility]);

  const showInTranscript = useCallback(() => updateWordVisibility(CLIP_UPDATE_TYPE.SHOW_WORD), [updateWordVisibility]);

  return (
    <>
      {areAllSelectedWordsVisible && (
        <TooltipV2
          place="left"
          tooltipId="hide-in-transcript-tooltip"
          tooltipContent="Hide in captions"
          size="xsmall"
          tooltipElement={
            <IconButton
              icon="IconEyeOff"
              trackingId="icon-hide-transcript"
              onClick={hideInTranscript}
              variation="checkbox"
              disabled={clipCaptionsLoading}
              buttonClassName="w-10 h-10"
              data-tooltip-id="hide-in-transcript-tooltip"
            />
          }
        />
      )}

      {areAllSelectedWordsHidden && (
        <TooltipV2
          place="left"
          tooltipId="show-in-transcript-tooltip"
          tooltipContent="Show in captions"
          size="xsmall"
          tooltipElement={
            <IconButton
              icon="IconEye"
              trackingId="icon-show-transcript"
              onClick={showInTranscript}
              variation="checkbox"
              disabled={clipCaptionsLoading}
              buttonClassName="w-10 h-10"
              data-tooltip-id="show-in-transcript-tooltip"
            />
          }
        />
      )}
    </>
  );
}

export default memo(TranscriptHideMenuOptions);
